<template>
  <v-dialog scrollable v-model="show">
    <v-card>
      <v-card-title class="grey lighten-3">Contact Suppliers</v-card-title>
      <v-card-text class="pa-3">
        <v-data-table
          v-model="selectedItems"
          :items="sortedContactSuppliersItems"
          class="elevation-1"
          :loading="$apollo.loading"
          :headers="visibleHeaders"
          v-bind="pagingOptions"
          show-select
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>
                  <v-layout align-center>
                    <v-checkbox
                      :input-value="selectedItems.indexOf(item) != -1"
                      primary
                      hide-details
                      class="pa-0 ma-0"
                      @click.stop="
                        checkSendInGroup(
                          selectedItems.indexOf(item) != -1,
                          item
                        )
                      "
                    ></v-checkbox>
                  </v-layout>
                </td>
                <td>
                  <v-icon>{{ supplierTypeIcon(item.serviceType) }}</v-icon>
                </td>
                <td>{{ item.date | formatDate }}</td>
                <td v-if="contactHeadOffice">{{ item.headOfficeName }}</td>
                <td>{{ item.supplierName }}</td>
                <td>{{ item.serviceName }}</td>
                <td>{{ item.supplierEmail }}</td>
                <td v-if="contactHeadOffice">
                  <v-checkbox
                    v-if="item.headOfficeName != ''"
                    v-model="item.useHeadOfficeEmail"
                    :label="item.headOfficeEmail"
                    @change="onUseHeadOfficeEmailChange(item)"
                    :disabled="item.headOfficeName == ''"
                  ></v-checkbox>
                </td>
                <td>
                  <v-btn icon small class="mx-0" @click="onEdit(item)">
                    <v-icon color="teal" small>mdi-pencil</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
        <div v-if="debug">
          <code>{{ contactSuppliersItems }}</code>
        </div>
      </v-card-text>
      <v-card-actions class="grey lighten-4">
        <v-layout grid-list-lg align-center>
          <v-checkbox
            v-model="sendCc"
            class="mr-2 mt-3"
            label="CC to:"
          ></v-checkbox>
          <v-text-field
            v-model="ccToEmail"
            :disabled="!sendCc"
            :error-messages="ccEmailErrors"
            :outlined="outlinedInputs"
            class="mb-0 mt-4"
            dense
          ></v-text-field>

          <v-select
            label="Select Email Type"
            :items="availableEmailTypes"
            v-model="selectedEmailType"
            item-value="value"
            class="mx-3 mt-4"
            :outlined="outlinedInputs"
            dense
          ></v-select>
          <v-checkbox
            v-model="sendLink"
            class="pr-3 right-aligned"
            label="Send Request/Cancel Link"
          ></v-checkbox>

          <v-btn color="warning" class="ml-3" text @click="closeDialog"
            >Close</v-btn
          >
          <v-btn :disabled="sendDisabled" @click="onPreview" class="mx-3">
            Preview <v-icon right small dark>mdi-file-find</v-icon></v-btn
          >
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="sendDisabled"
            @click="onSend"
          >
            Send <v-icon right small dark>mdi-send</v-icon></v-btn
          >
        </v-layout>
      </v-card-actions>

      <v-dialog v-model="warning" persistent width="300">
        <v-card-title
          >Please set email address for each selected service.</v-card-title
        >
        <v-layout>
          <v-flex class="text-right">
            <v-btn color="primary" @click="warning = false">Ok</v-btn>
          </v-flex>
        </v-layout>
      </v-dialog>
      <v-dialog v-model="loading" persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            Sending Messages...
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="contactEditDialog" persistent width="600">
        <v-card>
          <itinerary-details-contact-suppliers-edit
            :contact-item="contactEditItem"
            :default-cc="sendCc ? ccToEmail : ''"
            :dialog="contactEditDialog"
            @closeContactEditDialog="contactEditDialog = false"
            @onSaveEdit="onContactItemEdit"
          ></itinerary-details-contact-suppliers-edit>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  NON_ALLOCATED_SERVICES_SEARCH_QUERY,
  AMENDABLE_SERVICES_SEARCH_QUERY,
  CANCELABLE_SERVICES_SEARCH_QUERY,
  SEND_CONFIRMATION_MESSAGE_MUTATION,
  SEND_AMENDMENT_MESSAGE_MUTATION,
  SEND_CANCELLATION_MESSAGE_MUTATION,
} from "../../../../constants/graphql";
import ItineraryDetailsContactSuppliersEdit from "./ItineraryDetailsContactSuppliersEdit";
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import { required } from "vuelidate/lib/validators";
import { EmailsValidator } from "../../../../helpers/validators/emails-validator";
import { sortByDate, pagingOptions } from "../../../../helpers";
import gql from "graphql-tag";

let randomize = require("randomatic");
const GET_PREVIEW = gql`
  mutation getPreview($input: SupplierEmailPreviewInput!) {
    getSupplierEmailPreview(input: $input)
  }
`;

export default {
  name: "ItineraryDetailsContactSuppliersList",
  components: { ItineraryDetailsContactSuppliersEdit },
  mixins: [SupplierTypeIconMixin],
  props: {
    itineraryId: {
      type: String,
      default: "",
    },
    messageType: {
      type: String,
      default: "",
    },

    show: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      debug: false,
      pagingOptions,
      contactSuppliersItems: [],
      contactEditItem: {},
      selectedItems: [],
      contactEditDialog: false,
      sendMessageMutation: "",
      loading: false,
      sendCc: this.$store.getters.hasAccessTo("suppliers:cc_emails_default_on"),
      sendLink: this.$store.getters.hasAccessTo("suppliers:send_request_link"),
      warning: false,
      ccToEmail: this.$store.getters.userEmail,
      lastMessageType: "",
      emailTypes: [
        {
          text: "Standard",
          value: "standard",
        },
        {
          text: "Standard (EI)",
          value: "standard_ei",
        },
        {
          text: "Long Haul",
          value: "long_haul",
        },
        {
          text: "Student",
          value: "student",
        },
      ],
      tableHeaders: [
        {
          text: "Type",
          align: "left",
          name: "serviceType",
          sortable: true,
          value: "serviceType",
        },
        {
          text: "Date",
          align: "left",
          name: "date",
          sortable: true,
          value: "date",
          sort: sortByDate,
        },
        {
          text: "Head Office",
          align: "left",
          name: "headOfficeName",
          sortable: true,
          value: "headOfficeName",
        },
        {
          text: "Supplier",
          align: "left",
          name: "supplierName",
          sortable: true,
          value: "supplierName",
        },
        {
          text: "Service",
          align: "left",
          name: "serviceName",
          sortable: true,
          value: "serviceName",
        },
        {
          text: "Supplier Email",
          align: "left",
          name: "supplierEmail",
          sortable: true,
          value: "supplierEmail",
        },
        {
          text: "Head Office Email",
          align: "left",
          name: "headOfficeEmail",
          sortable: true,
          value: "headOfficeEmail",
        },
        {
          text: "Edit",
          value: "name",
          sortable: true,
          name: "edit",
          align: "left",
        },
      ],
      selectedEmailType:
        ["EuropeIncoming", "eitradetrav"].indexOf(
          this.$store.getters.userConfig.domain
        ) != -1
          ? "standard_ei"
          : "standard",
    };
  },
  computed: {
    visibleHeaders() {
      return this.contactHeadOffice
        ? this.tableHeaders
        : this.tableHeaders.filter((header) => {
            return (
              header.name != "headOfficeName" &&
              header.name != "headOfficeEmail"
            );
          });
    },
    contactHeadOffice() {
      return this.$store.getters.hasAccessTo("itineraries:contact_ho");
    },
    availableEmailTypes() {
      //console.log("availableEmailTypes", this.$store.getters.hasAccessTo('suppliers:email_types',false));
      if (this.$store.getters.hasAccessTo("suppliers:email_types", false)) {
        //https://tracker.globetrack.ie/issues/6561
        return this.emailTypes.filter((item) => {
          return item.value == "standard"
            ? ["EuropeIncoming", "eitradetrav"].indexOf(
                this.$store.getters.userConfig.domain
              ) == -1
            : true;
        });
      }
      return [
        {
          text: "Standard",
          value: "standard",
        },
      ];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    messagesToSend() {
      return this.selectedItems.map((item) => {
        return {
          groupId: item.groupId,
          serviceId: item.serviceId,
          supplierEmail: item.useHeadOfficeEmail
            ? item.headOfficeEmail
            : item.supplierEmail,
          cc: this.sendCc ? this.ccToEmail : "",
          subject: item.subject,
          notes: item.notes,
          emailType: this.selectedEmailType,
          sendLink: this.sendLink,
          useHeadOffice: item.useHeadOfficeEmail,
        };
      });
    },
    currentQuery() {
      if (this.messageType == "confirm") {
        return NON_ALLOCATED_SERVICES_SEARCH_QUERY;
      } else if (this.messageType == "amendment") {
        return AMENDABLE_SERVICES_SEARCH_QUERY;
      } else {
        return CANCELABLE_SERVICES_SEARCH_QUERY;
      }
    },
    currentMutation() {
      if (this.messageType == "confirm") {
        return SEND_CONFIRMATION_MESSAGE_MUTATION;
      } else if (this.messageType == "amendment") {
        return SEND_AMENDMENT_MESSAGE_MUTATION;
      } else {
        return SEND_CANCELLATION_MESSAGE_MUTATION;
      }
    },
    sortedContactSuppliersItems() {
      return this.contactSuppliersItems.sort(sortByDate);
    },
    ccEmailErrors() {
      let errors = [];
      if (this.sendCc) {
        if (!this.$v.ccToEmail.$dirty) {
          return errors;
        }
        if (!this.$v.ccToEmail.required) {
          errors.push("Email is required.");
        }
        if (!this.$v.ccToEmail.EmailsValidator) {
          errors.push("Input must be an email.");
        }
        return errors;
      } else {
        return [];
      }
    },
    checkValidity() {
      if (this.checkValidators) {
        this.messagesToSend.forEach((item) => {
          if (item.supplierEmail == "") {
            this.warning = true;
          }
        });
        return !this.warning;
      } else {
        return false;
      }
    },
    checkValidators() {
      if (this.sendCc) {
        this.$v.$touch();
        return !this.$v.$invalid;
      } else {
        return true;
      }
    },
    sendDisabled() {
      return this.selectedItems.length == 0;
    },
  },
  methods: {
    onUseHeadOfficeEmailChange(payload) {
      //update other items in the group
      for (const item of this.contactSuppliersItems) {
        if (item.groupId == payload.groupId) {
          console.log(item);
          item.useHeadOfficeEmail = payload.useHeadOfficeEmail;
        }
      }
      this.contactSuppliersItems = [...this.contactSuppliersItems];
    },
    closeDialog() {
      this.$emit("close");
    },
    onEdit(item) {
      this.contactEditItem = { ...item };
      this.contactEditDialog = true;
    },
    onContactItemEdit(editedItem) {
      this.contactEditDialog = false;
      for (const item of this.contactSuppliersItems) {
        if (item.id == editedItem.id) {
          item.notes = editedItem.notes;
          item.subject = editedItem.subject;
          item.supplierEmail = editedItem.supplierEmail;
          item.cc = editedItem.cc;
          item.headOfficeEmail = editedItem.headOfficeEmail;
          item.useHeadOfficeEmail = editedItem.useHeadOfficeEmail;
          if (
            item.supplierEmail != "" &&
            this.selectedItems.indexOf(item) == -1
          ) {
            this.addGroupItemsToSelected(item.groupId);
          }
        } else if (item.groupId == editedItem.groupId) {
          item.supplierEmail = editedItem.supplierEmail;
          item.headOfficeEmail = editedItem.headOfficeEmail;
          item.useHeadOfficeEmail = editedItem.useHeadOfficeEmail;
        }
      }
      this.contactSuppliersItems = [...this.contactSuppliersItems];
    },
    onPreview() {
      if (this.checkValidity) {
        const input = {
          itineraryId: this.itineraryId,
          items: this.messagesToSend,
          emailType: this.messageType,
        };

        this.$apollo
          .mutate({
            mutation: GET_PREVIEW,
            variables: { input },
          })
          .then((res) =>
            window.open(res.data.getSupplierEmailPreview, "_blank")
          )
          .catch((err) => console.error(err));
      }
    },
    onSend() {
      if (this.checkValidity) {
        this.loading = true;

        this.$apollo
          .mutate({
            mutation: this.currentMutation,
            variables: {
              itineraryId: this.itineraryId,
              items: this.messagesToSend,
            },
          })
          .then(this.sendEmailHandler, this.sendEmailErrorHandler);
      }
    },

    sendEmailHandler(result) {
      this.loading = false;
      this.$store.dispatch("showAppSnackbar", { msg: "Messages Sent" });
      this.closeDialog();
    },
    sendEmailErrorHandler(err) {
      this.loading = false;
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    checkSendInGroup(isAlreadySelected, currentItem) {
      if (!isAlreadySelected && currentItem.supplierEmail == "") {
        this.onEdit(currentItem);
      } else {
        if (isAlreadySelected) {
          this.selectedItems = this.selectedItems.filter(
            (item) => item.groupId != currentItem.groupId
          );
        } else {
          this.addGroupItemsToSelected(currentItem.groupId);
        }
      }
    },
    addGroupItemsToSelected(groupId) {
      let groupItems = this.contactSuppliersItems.filter(
        (item) => item.groupId == groupId
      );
      this.selectedItems = this.selectedItems.concat(groupItems);
    },
  },
  apollo: {
    contactSupplierItems: {
      query() {
        return this.currentQuery;
      },
      variables() {
        return {
          itineraryId: this.itineraryId,
        };
      },
      skip() {
        return this.messageType == "";
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.contactSuppliersItems = data.contactSupplierItems.edges.map(
          (item) => {
            return {
              id: randomize("Aa0", 32),
              date: item.node.date,
              groupId: item.node.groupId,
              isExtra: item.node.isExtra,
              serviceId: item.node.serviceId,
              serviceName: item.node.serviceName,
              serviceType: item.node.serviceType,
              supplierEmail: item.node.supplierEmail,
              supplierName: item.node.supplierName,
              cc: "",
              subject: "",
              notes: "",
              sendLink: true,
              emailType: "",
              headOfficeEmail:
                item.node.supplier && item.node.supplier.headOffice
                  ? item.node.supplier.headOffice.email
                  : "",
              headOfficeName:
                item.node.supplier && item.node.supplier.headOffice
                  ? item.node.supplier.headOffice.name
                  : "",
              useHeadOfficeEmail: false,
            };
          }
        );
        this.contactSuppliersItems.forEach((item) => {
          if (item.supplierEmail != "") {
            //this.selectedItems.push(item); https://tracker.globetrack.ie/issues/8390
          }
        });
      },
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.selectedItems = [];
      }
    },
    dialog(value) {
      if (value) {
        this.ccToEmail = this.$store.getters.userEmail;
        this.sendCc = true;
        this.sendLink = true;
        this.$v.$reset();
        // reset selected items
        this.selectedItems = [];
        if (
          this.$apollo.queries.contactSupplierItems != null &&
          this.lastMessageType == this.messageType
        ) {
          this.$apollo.queries.contactSupplierItems.refetch();
        }
      } else {
        this.lastMessageType = this.messageType;
      }
    },
  },
  validations: {
    ccToEmail: { required, EmailsValidator },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/contact-suppliers/ItineraryDetailsContactSuppliersList.vue